import {useCallback} from 'react';
import {useQuoteField} from './useHandleQuoteFieldChange';
import {List as IList, Map as IMap} from 'immutable';
import {createRangeValues, createSingleValue} from '../QuoteForm/Plan/PlanTemplates/GWL/Plan1';
import {ReducerState} from 'rootReducer';
import {useSelector, useDispatch} from 'react-redux';
import {GPLU_WHOLE_LIFE_FIELD_ID, GPLUCoverageCode} from '../QuoteForm/Plan/PlanTemplates/GWL/gpluConstants';
import {removeQuoteFormErrorEntries} from 'Quoting/ducks/createQuote';

const enum BenefitType {
  MultipleValues = 'MultipleValues',
  RangeValues = 'RangeValues',
  SingleValue = 'SingleValue',
}

export const useHandleGWLBenefitTypeCdChange = (uwAmount: any, coverageName: GPLUCoverageCode) => {
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {selectedPlan, selectedCoverageIndex, defaultPlanValues} = useSelector(
    (state: ReducerState) => state.quoteForm
  );

  const dispatch = useDispatch();

  return useCallback(
    (benefitTypeCd: string, propertyPaths: Record<string, string>) => {
      const _key = getQuoteFieldValue<IMap<string, any>>(
        `benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}].coverageDefs[${selectedCoverageIndex}]._key`
      );

      const selectedPlanCd = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}].planCd`);
      const defaultValues = defaultPlanValues?.get(selectedPlanCd)?.get(coverageName);
      const [rootId, parentId] = [_key?.get('rootId'), _key?.get('id')];

      switch (benefitTypeCd) {
        case BenefitType.MultipleValues: {
          updateQuoteFieldValues([
            {path: propertyPaths.benefitTypeCd, newValue: benefitTypeCd},
            {path: propertyPaths.rangeValues, newValue: undefined},
            {path: propertyPaths.singleValue, newValue: undefined},
          ]);
          break;
        }
        case BenefitType.RangeValues: {
          const updatedRangeValues = createRangeValues(defaultValues, rootId, parentId, uwAmount);
          updateQuoteFieldValues([
            {path: propertyPaths.benefitTypeCd, newValue: benefitTypeCd},
            {path: propertyPaths.rangeValues, newValue: updatedRangeValues},
            {path: propertyPaths.multipleValues, newValue: IList()},
            {path: propertyPaths.singleValue, newValue: undefined},
            (coverageName == GPLUCoverageCode.WHOLE_LIFE) && {
              path: propertyPaths.maxDeathBenefitAmountAllowance,
              newValue: updatedRangeValues.benefitMaxAmount,
            },
            (coverageName == GPLUCoverageCode.WHOLE_LIFE) && {
              path: propertyPaths.minDeathBenefitAmountAllowance,
              newValue: updatedRangeValues.benefitMinAmount,
            },
          ]);
          break;
        }
        case BenefitType.SingleValue: {
          const singleValue = createSingleValue(
            rootId,
            parentId,
            uwAmount,
            defaultPlanValues?.get(selectedPlanCd)?.get(GPLUCoverageCode.WHOLE_LIFE)
          );

          updateQuoteFieldValues([
            {path: propertyPaths.benefitTypeCd, newValue: benefitTypeCd},
            {path: propertyPaths.singleValue, newValue: singleValue},
            {path: propertyPaths.rangeValues, newValue: undefined},
            {path: propertyPaths.multipleValues, newValue: IList()},
            (coverageName == GPLUCoverageCode.WHOLE_LIFE) && {
              path: propertyPaths.maxDeathBenefitAmountAllowance,
              newValue: singleValue?.flatBenefitAmount,
            },
            (coverageName == GPLUCoverageCode.WHOLE_LIFE) && {
              path: propertyPaths.minDeathBenefitAmountAllowance,
              newValue: singleValue?.flatBenefitAmount,
            },
          ]);
          break;
        }
      }
      dispatch(
        removeQuoteFormErrorEntries([
          `${GPLU_WHOLE_LIFE_FIELD_ID.benefitMaxAmount}__${selectedPlan?.planKey}`,
          `${GPLU_WHOLE_LIFE_FIELD_ID.benefitMinAmount}__${selectedPlan?.planKey}`,
          `${GPLU_WHOLE_LIFE_FIELD_ID.incrementAmount}__${selectedPlan?.planKey}`,
          `${GPLU_WHOLE_LIFE_FIELD_ID.flatBenefitAmount}__${selectedPlan?.planKey}`,
          `${GPLU_WHOLE_LIFE_FIELD_ID.specifiedBenefitAmount}__${selectedPlan?.planKey}`,
        ])
      );
    },
    [selectedPlan, selectedCoverageIndex, defaultPlanValues]
  );
};
